import { useLocation } from 'react-router-dom';

import { useDateCheck } from './useDateCheck';

import { BOOK_A_DEMO_LINK, TARGET_DATE } from 'constants/common';

import {
  autoDockingMeta,
  automatedDataPipelinesMeta,
  intelligentSystemTestingApplicationMeta,
  airbusHelicopterCustomerCaseMeta,
  reinforcementLearningApplicationMeta,
  airbusUpNextCustomerCaseMeta,
  platformMeta,
  resourcesMeta,
  intelligentDataPipelineApplicationMeta,
  hondaMarineCustomerCaseMeta,
  automaticDatasetEnhancementApplicationMeta,
  brunswickUseCaseCustomerCaseMeta,
  careersMeta,
} from 'constants/pages';

import { ContactFormType } from 'types/contact-us';
import {
  aeronauticsData,
  maritimeData,
  mobileData,
  navyDefenceData,
} from 'constants/industries/industries';
import { TPaddingOptions } from 'types/common';

const contactFormTypeOptions = new Map<string, ContactFormType>([
  [resourcesMeta.url, ContactFormType.RESOURCES],
  [autoDockingMeta.url, ContactFormType.AUTO_DOCKING],
  [automatedDataPipelinesMeta.url, ContactFormType.PIPELINE_DATA],
  [platformMeta.url, ContactFormType.PLATFORM],

  [intelligentSystemTestingApplicationMeta.url, ContactFormType.APPLICATIONS],
  [intelligentDataPipelineApplicationMeta.url, ContactFormType.APPLICATIONS],
  [automaticDatasetEnhancementApplicationMeta.url, ContactFormType.APPLICATIONS],
  [reinforcementLearningApplicationMeta.url, ContactFormType.APPLICATIONS],

  [airbusHelicopterCustomerCaseMeta.url, ContactFormType.CUSTOMER_CASE],
  [hondaMarineCustomerCaseMeta.url, ContactFormType.CUSTOMER_CASE],
  [brunswickUseCaseCustomerCaseMeta.url, ContactFormType.CUSTOMER_CASE],
  [airbusUpNextCustomerCaseMeta.url, ContactFormType.CUSTOMER_CASE],

  [maritimeData.meta.url, ContactFormType.MARITIME],
  [navyDefenceData.meta.url, ContactFormType.NAVY_DEFENCE],
  [aeronauticsData.meta.url, ContactFormType.AERONAUTICS],
  [mobileData.meta.url, ContactFormType.MOBILE_MACHINES],

  [careersMeta.url, ContactFormType.CAREERS],
]);

interface TypeData {
  title: string;
  description: string;
  buttonLabel: string;
  additionalText?: string;
  additionalButton?: string;
  additionalButtonLink?: string;
  buttonLink?: string;
  padding: TPaddingOptions;
}

const defaultPadding: TPaddingOptions = {
  md: '120px 0',
  sm: '80px 0',
  xxs: '80px 16px',
};

const applicationDescriptions = new Map<string, string>([
  [
    intelligentSystemTestingApplicationMeta.url,
    'Discover how our intelligent system testing uses scenario-based simulations, mirroring real-world operational conditions, to optimize autonomous docking systems with advanced auto-docking capabilities.',
  ],

  [
    intelligentDataPipelineApplicationMeta.url,
    'This live demo showcases how AILiveSim enhances maritime surveillance by automating vessel detection and classification — even with limited-resolution data. Experience how intelligent simulation boosts model accuracy and drives reliable results through a fully automated pipeline.',
  ],
  [
    automaticDatasetEnhancementApplicationMeta.url,
    'See how AILiveSim intelligently enhances your datasets. Our live demo shows how automated, scenario-based simulation augments real-world data for more accurate, resilient AI — without manual labeling or costly field campaigns.',
  ],

  [
    reinforcementLearningApplicationMeta.url,
    'See how AILiveSim enables faster, smarter reinforcement learning for autonomous control. Our live demo showcases how simulation-based RL helps train machines to navigate real-world complexity with precision, safety, and speed.',
  ],
]);

const getParameters = (
  type: ContactFormType,
  isTargetDate: boolean,
  isCTABlock: boolean,
  pathname: string,
) => {
  const data = new Map<ContactFormType, TypeData>([
    [
      ContactFormType.DEFAULT,
      {
        title: 'Discover the benefits of synthetic data and simulation',
        description: '',
        buttonLabel: 'Contact us',
        padding: defaultPadding,
      },
    ],
    [
      ContactFormType.ARTICLES,
      {
        title: 'Discover the benefits of synthetic data and simulation',
        description: '',
        buttonLabel: 'Contact us',
        additionalButton: 'Explore Applications',
        additionalButtonLink: '/all-applications',
        padding: defaultPadding,
      },
    ],
    [
      ContactFormType.PLATFORM,
      {
        title: 'Discover the benefits of synthetic data and simulation',
        description: '',
        buttonLabel: 'Contact us',
        padding: {
          xxs: '120px 16px',
        },
      },
    ],
    [
      ContactFormType.AUTO_DOCKING,
      {
        title: 'Ready to Revolutionize Your Maritime AI Training?',
        description: `Don't miss this opportunity to gain a competitive edge. ${
          isTargetDate
            ? ''
            : 'Be one of the first 10 participants to explore AILiveSim’s intelligent simulation platform.'
        }`,
        buttonLabel: 'Contact us',
        padding: defaultPadding,
      },
    ],
    [
      ContactFormType.PIPELINE_DATA,
      {
        title: 'Ready to Revolutionize Your Maritime AI Training?',
        description: `${
          isTargetDate ? '' : 'Secure your spot in this limited beta program.'
        } Gain a competitive advantage by leveraging intelligent simulation to build more robust and accurate maritime AI, faster than ever before. Help shape the future of AI training in the maritime domain.`,
        buttonLabel: 'Contact us',
        padding: defaultPadding,
      },
    ],
    [
      ContactFormType.APPLICATIONS,
      {
        title: 'Request a Demo! See It in Action',
        description:
          'Discover how our intelligent system testing uses scenario-based simulations, mirroring real-world operational conditions, to optimize autonomous docking systems with advanced auto-docking capabilities.',
        buttonLabel: 'Book a demo',
        additionalText: '...to see AILiveSim in action',
        buttonLink: BOOK_A_DEMO_LINK,
        padding: {
          sm: '64px 64px',
          xs: '64px 16px',
          xxs: '64px 16px',
        },
      },
    ],
    [
      ContactFormType.RESOURCES,
      {
        title: !isCTABlock
          ? 'Discover the benefits of synthetic data and simulation'
          : 'Stay Updated with Resources',
        description: !isCTABlock ? '' : 'Receive the latest news and insights directly.',
        buttonLabel: !isCTABlock ? 'Contact us' : 'Subscribe',
        additionalButton: 'Explore Applications',
        additionalButtonLink: '/all-applications',
        padding: {
          xxs: '120px 16px',
        },
      },
    ],
    [
      ContactFormType.CUSTOMER_CASE,
      {
        title: 'Ready to Enhance Safety?',
        description:
          'Discover how AILiveSim can revolutionize your AI safety system testing and validation.',
        buttonLabel: 'Book a demo',
        buttonLink: BOOK_A_DEMO_LINK,
        padding: {
          xxs: '112px 16px',
        },
      },
    ],
    [ContactFormType.MARITIME, maritimeData.contactForm],
    [ContactFormType.NAVY_DEFENCE, navyDefenceData.contactForm],
    [ContactFormType.AERONAUTICS, aeronauticsData.contactForm],
    [ContactFormType.MOBILE_MACHINES, mobileData.contactForm],
    [
      ContactFormType.CAREERS,
      {
        title: 'Let’s Shape the Future of AI Together',
        description:
          'Join a team where professionalism meets passion, and where your work makes a difference.',
        buttonLabel: 'Explore Open Role',
        additionalButton: 'Discover Our Platform',
        additionalButtonLink: platformMeta.url,
        padding: {
          xxs: '112px 16px',
        },
      },
    ],
  ]);
  const {
    description = '',
    title = '',
    additionalText = '',
    buttonLabel = '',
    additionalButton = '',
    additionalButtonLink = '',
    buttonLink = '',
    padding,
  } = data.get(type) || {};

  const isApplicationPage = type === ContactFormType.APPLICATIONS;
  const desc = isApplicationPage ? applicationDescriptions.get(pathname) || '' : description;

  return {
    description: desc,
    title,
    additionalText,
    buttonLabel,
    type,
    additionalButton,
    additionalButtonLink,
    buttonLink,
    padding,
  };
};

export const useContactFormData = (isCTABlock: boolean) => {
  const location = useLocation();

  let type = contactFormTypeOptions.get(location.pathname);

  if (!type) {
    // Check if pathname starts with /articles/, /partnerships/, or /news/
    if (
      location.pathname.startsWith('/articles/') ||
      location.pathname.startsWith('/partnerships/') ||
      location.pathname.startsWith('/news/')
    ) {
      type = ContactFormType.ARTICLES;
    } else {
      type = ContactFormType.DEFAULT;
    }
  }

  const { isTargetDate } = useDateCheck(TARGET_DATE);

  return getParameters(type, isTargetDate, isCTABlock, location.pathname);
};
